// Building a campaign by hand: a blank one, adding a post, Story, text or
// email on a day, and moving everything when the dates change.
import moment from 'moment'
import { realPost, showcaseFor } from '../../social/data/postTypes'
import { templateFor } from '../../plans/data/emailFor'
import { smsText, windowOf } from '../../plans/data/planner'

export const LENGTHS = [
  { days: 1, label: 'A day' },
  { days: 3, label: 'A weekend' },
  { days: 7, label: 'A week' },
  { days: 14, label: 'Two weeks' },
  { days: 21, label: 'Three weeks' },
  { days: 28, label: 'Four weeks' },
  { days: 42, label: 'Six weeks' },
]
export const MAX_DAYS = 42

let seq = 0
const nextId = (channel) => `${channel}-${Date.now().toString(36)}${(++seq).toString(36)}`
const TIMES = { post: [12, 30], story: [18, 0], sms: [18, 0], email: [10, 0] }
export const defaultTime = (channel, day) => {
  const [h, m] = TIMES[channel] || [12, 0]
  return moment(day).hours(h).minutes(m).seconds(0).milliseconds(0)
}

// A new thing on a day, with words to start from (nothing made up: no prices,
// no offers, no names)
export const newItem = (channel, day, { trade = 'hair', brandColour = null, family = 'editorial', salonName = '', name = '' } = {}) => {
  let at = defaultTime(channel, day)
  if (!at.isAfter(moment())) at = moment().add(1, 'hour').startOf('hour')
  const base = { id: nextId(channel), channel, at: at.toISOString(), on: true, added: true }
  if (channel === 'post' || channel === 'story') {
    // An announcement in their words, not a "fresh work" post with a stock
    // photo that would go out looking like the salon's own work
    const show = showcaseFor('announce')
    const post = { ...realPost('announce', { trade, family: brandColour ? family : show.family, colourway: brandColour ? 'brand' : show.colourway, brandColour, format: channel === 'story' ? 'story' : 'post', content: { eyebrow: name || '', headline: name || 'Book your *next visit*', body: '', cta: 'Book online, link in bio' } }), status: 'draft' }
    return { ...base, title: channel === 'story' ? 'Story' : 'Post', post }
  }
  if (channel === 'sms') {
    return { ...base, title: 'Text', message: smsText(['Hi {{FirstName}}, book your next visit at {{SalonName}}: {{BookingLink}}'], salonName).message }
  }
  return {
    ...base,
    title: 'Email',
    email: {
      templateId: templateFor('fill', trade),
      subject: name ? `${name} at {{SalonName}}` : 'News from {{SalonName}}',
      preheader: 'Book online in a minute.',
      hero: { eyebrow: name || '', headline: name || 'Time for *you*', body: 'Book in whenever suits you, online in a minute.', button: 'Book now', buttonUrl: '{{BookingLink}}' },
    },
  }
}

// A campaign started from nothing: tomorrow for a week, nothing in it yet
export const blankCampaign = (now = new Date()) => {
  const start = moment(now).startOf('day').add(1, 'day')
  return {
    key: null,
    kind: 'yours',
    title: 'New campaign',
    why: '',
    audience: { segment: 'all', label: 'All your clients', count: null },
    items: [],
    start: start.format('YYYY-MM-DD'),
    end: moment(start).add(6, 'days').format('YYYY-MM-DD'),
  }
}

export const lengthOf = (c) => (c.start && c.end ? moment(c.end).diff(moment(c.start), 'days') + 1 : 1)

// New dates: each thing keeps its place in the campaign (a third of the way
// through stays a third of the way through) and its time of day. Nothing
// lands in the past.
export const respread = (c, start, days, now = new Date()) => {
  const oldStart = moment(c.start || windowOf(c.items).start || start).startOf('day')
  const oldDays = Math.max(1, lengthOf(c))
  const s = moment(start).startOf('day')
  const n = Math.max(1, Math.min(MAX_DAYS, days))
  const items = c.items.map((it) => {
    const t = moment(it.at)
    const offset = Math.max(0, t.clone().startOf('day').diff(oldStart, 'days'))
    const day = oldDays <= 1 ? 0 : Math.round((offset / (oldDays - 1)) * (n - 1))
    let at = s.clone().add(Math.min(day, n - 1), 'days').hours(t.hours()).minutes(t.minutes()).seconds(0)
    if (!at.isAfter(now)) at = moment(now).add(1, 'hour').startOf('hour')
    return { ...it, at: at.toISOString() }
  })
  return { ...c, items, start: s.format('YYYY-MM-DD'), end: s.clone().add(n - 1, 'days').format('YYYY-MM-DD') }
}

// Items by day, in order: [{ day: 'YYYY-MM-DD', items }]
export const byDay = (items) => {
  const out = []
  items.slice().sort((a, b) => new Date(a.at) - new Date(b.at)).forEach((it) => {
    const d = moment(it.at).format('YYYY-MM-DD')
    const last = out[out.length - 1]
    if (last && last.day === d) last.items.push(it)
    else out.push({ day: d, items: [it] })
  })
  return out
}
